/**
 * RecipeBuilder — inline collapsible section on the Label Maker that lets
 * an operator describe a wet-weight recipe (e.g. Penne Alfredo: pasta 200 g,
 * cream 100 g, parmesan 50 g) and get back an estimated Nutrition Facts
 * panel for the FREEZE-DRIED finished product.
 *
 * Wires POST /api/label-presets/recipe-estimate. On success calls
 * `onEstimate({ recipe_name, ingredients_ordered, nutritionData, sources,
 * computed, narrative })` so the parent LabelMaker can apply the fields
 * to its label form and preview.
 */

import React, { useContext, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  ChefHat, ChevronDown, ChevronUp, Plus, Trash2, Sparkles, Loader2,
} from 'lucide-react';
import AIProgressIndicator from './AIProgressIndicator';

const DEFAULT_ROWS = [
  { name: '', wet_weight_g: '' },
  { name: '', wet_weight_g: '' },
];

export default function RecipeBuilder({ onEstimate }) {
  const { API } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [recipeName, setRecipeName] = useState('');
  const [rows, setRows]             = useState(DEFAULT_ROWS);
  const [finishedWeight, setFinishedWeight] = useState('');
  const [servingSize, setServingSize]       = useState('');
  const [loading, setLoading] = useState(false);
  // Local result summary so operators SEE the estimate landed even if
  // the label preview is scrolled off-screen. Cleared on next estimate.
  const [result, setResult] = useState(null);

  const setRow = (idx, key, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
  };
  const addRow    = () => setRows((prev) => [...prev, { name: '', wet_weight_g: '' }]);
  const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));

  const estimate = async () => {
    const ingredients = rows
      .map((r) => ({
        name: (r.name || '').trim(),
        wet_weight_g: Number(r.wet_weight_g) || 0,
      }))
      .filter((r) => r.name && r.wet_weight_g > 0);
    if (ingredients.length < 1) {
      toast.error('Add at least one ingredient with a wet weight in grams.');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const body = {
        recipe_name: recipeName.trim() || 'Freeze-Dried Recipe',
        ingredients,
      };
      const fw = Number(finishedWeight);
      const ss = Number(servingSize);
      if (fw > 0) body.total_finished_weight_g = fw;
      if (ss > 0) body.serving_size_g          = ss;
      const { data } = await axios.post(
        `${API}/label-presets/recipe-estimate`,
        body,
      );
      onEstimate({
        recipe_name:         data.recipe_name,
        ingredients_ordered: data.ingredients_ordered,
        nutritionData:       data.data,
        sources:             data.sources || [],
        computed:            data.computed || {},
        narrative:           data.narrative || '',
        disclaimer:          data.disclaimer || '',
      });
      setResult({
        recipe_name: data.recipe_name,
        computed:    data.computed || {},
        data:        data.data || {},
        sources:     data.sources || [],
        narrative:   data.narrative || '',
      });
      toast.success('Estimated — nutrition + ingredients applied to the label.');
      // Nudge the operator toward the live preview so they can visually
      // confirm the panel updated. Runs on the next frame so React has
      // committed the state change first.
      requestAnimationFrame(() => {
        document
          .querySelector('[data-testid="lm-nutrition-panel"]')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Recipe estimate failed');
    } finally { setLoading(false); }
  };

  return (
    <Card className="no-print mb-4" data-testid="lm-recipe-builder">
      <CardContent className="p-0">
        <button
          type="button"
          onClick={() => setOpen((x) => !x)}
          className="w-full flex items-center justify-between p-4 hover:bg-accent/50 transition-colors text-left"
          data-testid="lm-recipe-toggle"
        >
          <div className="flex items-center gap-2">
            <ChefHat className="w-5 h-5 text-primary" />
            <div>
              <div className="font-semibold text-sm">Recipe Mode</div>
              <div className="text-xs text-muted-foreground">
                List wet ingredients — AI estimates the finished (freeze-dried) label.
              </div>
            </div>
          </div>
          {open
            ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
            : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </button>

        {open && (
          <div className="p-4 pt-0 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-3">
                <Label className="text-xs">Recipe name</Label>
                <Input
                  value={recipeName}
                  onChange={(e) => setRecipeName(e.target.value)}
                  placeholder='e.g. "Penne Alfredo"'
                  data-testid="lm-recipe-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider">
                Ingredients — wet weights in grams
              </Label>
              {rows.map((row, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <Input
                    value={row.name}
                    onChange={(e) => setRow(idx, 'name', e.target.value)}
                    placeholder={idx === 0 ? 'Penne pasta, cooked' : 'Ingredient'}
                    data-testid={`lm-recipe-ing-name-${idx}`}
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    min="0"
                    step="0.1"
                    value={row.wet_weight_g}
                    onChange={(e) => setRow(idx, 'wet_weight_g', e.target.value)}
                    placeholder="grams (wet)"
                    data-testid={`lm-recipe-ing-weight-${idx}`}
                    className="w-32"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => removeRow(idx)}
                    disabled={rows.length <= 1}
                    aria-label="Remove ingredient"
                    data-testid={`lm-recipe-ing-remove-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addRow}
                data-testid="lm-recipe-ing-add"
              >
                <Plus className="w-4 h-4 mr-1" /> Add ingredient
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">
                  Finished pouch weight (g) <span className="text-muted-foreground">— optional</span>
                </Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={finishedWeight}
                  onChange={(e) => setFinishedWeight(e.target.value)}
                  placeholder="AI will estimate if blank"
                  data-testid="lm-recipe-finished-weight"
                />
              </div>
              <div>
                <Label className="text-xs">
                  Serving size (g) <span className="text-muted-foreground">— optional</span>
                </Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={servingSize}
                  onChange={(e) => setServingSize(e.target.value)}
                  placeholder="Defaults to 1 pouch = 1 serving"
                  data-testid="lm-recipe-serving-size"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                type="button"
                onClick={estimate}
                disabled={loading}
                data-testid="lm-recipe-estimate"
              >
                {loading
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <Sparkles className="w-4 h-4 mr-2" />}
                Estimate finished label
              </Button>
            </div>

            <AIProgressIndicator
              active={loading}
              estimatedMs={40000}
              data-testid="lm-recipe-progress"
              steps={[
                { at: 0,     label: 'Resolving each ingredient in USDA FoodData Central…' },
                { at: 8000,  label: 'Looking up moisture content per ingredient…' },
                { at: 18000, label: 'Summing macros and applying freeze-dry math…' },
                { at: 28000, label: 'Computing per-serving %DV against FDA 2016 targets…' },
                { at: 36000, label: 'Finalising panel & citing sources…' },
              ]}
            />

            {result && !loading && (
              <div
                className="rounded-md border border-green-500/40 bg-green-500/5 p-3 space-y-2"
                data-testid="lm-recipe-result"
              >
                <div className="flex items-center gap-2 text-sm font-semibold text-green-700 dark:text-green-400">
                  <ChefHat className="w-4 h-4" /> Applied to label — scroll to preview to confirm
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <ResultStat label="Recipe"       value={result.recipe_name} />
                  <ResultStat label="Serving"      value={`${(result.computed.serving_size_g || 0).toFixed?.(0) || result.computed.serving_size_g}g`} />
                  <ResultStat label="Calories"     value={result.data.calories || '—'} />
                  <ResultStat label="Protein"      value={result.data.protein_g ? `${result.data.protein_g}g` : '—'} />
                </div>
                {result.narrative && (
                  <p className="text-[11px] text-muted-foreground">
                    {result.narrative}
                  </p>
                )}
                {result.sources?.length > 0 && (
                  <div className="text-[10px] text-muted-foreground">
                    <span className="font-semibold uppercase tracking-wider">Sources:</span>{' '}
                    {result.sources.slice(0, 3).map((u, i) => (
                      <a
                        key={i} href={u} target="_blank" rel="noopener noreferrer"
                        className="text-primary hover:underline mr-2 break-all"
                      >
                        {(() => { try { return new URL(u).hostname.replace('www.', ''); } catch { return u; } })()}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ResultStat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-semibold text-foreground truncate" title={String(value)}>{value || '—'}</div>
    </div>
  );
}
